import { Sheet, SheetContent } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { RentalLead, RentalListing, PLATFORMS } from "@/hooks/use-rentals";
import {
  X,
  Sparkles,
  MessageSquare,
  Calendar,
  FileText,
  CheckCircle2,
  XCircle,
  MinusCircle,
  Mail,
  Phone,
  Building2,
  Archive,
} from "lucide-react";

// ── Status config ──────────────────────────────────────────────────────────
// The pickable pipeline. "archived" is deliberately absent: it's set by the
// database when a listing is deleted, never chosen by hand.
const ALL_STATUSES: RentalLead["status"][] = [
  "new",
  "contacted",
  "showing_scheduled",
  "application_sent",
  "approved",
  "denied",
  "lost",
];

const STATUS_META: Record<
  RentalLead["status"],
  { label: string; icon: React.ReactNode; active: string; header: string }
> = {
  new: {
    label: "New",
    icon: <Sparkles className="w-3.5 h-3.5" />,
    active: "bg-blue-500/15 text-blue-400 border-blue-500/32",
    header: "bg-blue-500/15 text-blue-400 border-blue-500/32",
  },
  contacted: {
    label: "Contacted",
    icon: <MessageSquare className="w-3.5 h-3.5" />,
    active: "bg-amber-400/15 text-amber-400 border-amber-400/32",
    header: "bg-amber-400/15 text-amber-400 border-amber-400/32",
  },
  showing_scheduled: {
    label: "Scheduled",
    icon: <Calendar className="w-3.5 h-3.5" />,
    active: "bg-green-500/15 text-green-400 border-green-500/32",
    header: "bg-green-500/15 text-green-400 border-green-500/32",
  },
  application_sent: {
    label: "Applied",
    icon: <FileText className="w-3.5 h-3.5" />,
    active: "bg-primary/15 text-primary border-primary/32",
    header: "bg-primary/15 text-primary border-primary/32",
  },
  approved: {
    label: "Approved",
    icon: <CheckCircle2 className="w-3.5 h-3.5" />,
    active: "bg-green-500/15 text-green-400 border-green-500/32",
    header: "bg-green-500/15 text-green-400 border-green-500/32",
  },
  denied: {
    label: "Declined",
    icon: <XCircle className="w-3.5 h-3.5" />,
    active: "bg-destructive/15 text-destructive border-destructive/32",
    header: "bg-destructive/15 text-destructive border-destructive/32",
  },
  lost: {
    label: "Lost",
    icon: <MinusCircle className="w-3.5 h-3.5" />,
    active: "bg-muted text-muted-foreground border-border",
    header: "bg-muted text-muted-foreground border-border",
  },
  // Set by the DB when the listing was deleted — history, not a pipeline stage.
  archived: {
    label: "Listing deleted",
    icon: <Archive className="w-3.5 h-3.5" />,
    active: "bg-muted text-muted-foreground border-border",
    header: "bg-muted text-muted-foreground border-border",
  },
};

// ── Helpers ────────────────────────────────────────────────────────────────
const platformPip: Record<string, { bg: string; initial: string }> = {
  zillow:         { bg: "bg-[#006aff]", initial: "Z" },
  apartments_com: { bg: "bg-[#00838f]", initial: "A" },
  realtor_com:    { bg: "bg-[#d92228]", initial: "R" },
  trulia:         { bg: "bg-[#00b04f]", initial: "T" },
  rent_com:       { bg: "bg-[#f5a623]", initial: "R" },
  facebook:       { bg: "bg-[#1877f2]", initial: "F" },
  craigslist:     { bg: "bg-[#5d2da9]", initial: "C" },
};

function fmtMoney(n: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency", currency: "USD", maximumFractionDigits: 0,
  }).format(n);
}

function fmtDate(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso + "T12:00:00").toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "numeric",
  });
}

function fmtRelDays(iso: string | null | undefined) {
  if (!iso) return "—";
  const days = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86400000));
  return days === 0 ? "today" : days === 1 ? "yesterday" : `${days} days ago`;
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[10.5px] font-bold uppercase tracking-[0.06em] text-muted-foreground mb-3">
      {children}
    </div>
  );
}

// ── Props ──────────────────────────────────────────────────────────────────
interface Props {
  lead: RentalLead | null;
  listing: RentalListing | null;
  propData: any;
  /**
   * Name of the applicant already approved on this listing, when it isn't this
   * one. The unit is theirs until they're moved off Approved, so "Approved" is
   * not offered here — useRentals refuses it anyway.
   */
  approvalBlockedBy?: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpdateStatus: (status: RentalLead["status"]) => void;
}

export function ApplicationDetailSheet({
  lead,
  listing,
  propData,
  approvalBlockedBy,
  open,
  onOpenChange,
  onUpdateStatus,
}: Props) {
  if (!lead) return null;

  const meta  = STATUS_META[lead.status];
  const initials = lead.name.split(" ").map((p) => p[0]).filter(Boolean).join("").slice(0, 2).toUpperCase();
  const pip = lead.platform ? platformPip[lead.platform] : null;
  const pipLabel = lead.platform ? (PLATFORMS.find((p) => p.id === lead.platform)?.name ?? lead.platform) : null;

  const addr1 = propData?.address || propData?.street_address || listing?.title || "—";
  const heroPhoto = listing?.photos?.[0];

  const leaseLabel = listing?.lease_term_months === 0
    ? "Month-to-month"
    : listing?.lease_term_months
      ? `${listing.lease_term_months}-month lease`
      : null;

  const showingDate = lead.showing_date;
  const note = lead.message || lead.notes;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full max-w-[480px] p-0 flex flex-col gap-0 border-l border-border"
        hideCloseButton
      >
        {/* ── Header ── */}
        <header className="px-[22px] py-4 border-b border-border flex items-center gap-3 flex-shrink-0">
          {/* Avatar */}
          <div className="w-11 h-11 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 text-white grid place-items-center text-[16px] font-bold flex-shrink-0">
            {initials}
          </div>
          {/* Name + sub */}
          <div className="flex-1 min-w-0">
            <div className="text-[18px] font-bold text-foreground leading-snug truncate">{lead.name}</div>
            <div className="text-[13px] text-muted-foreground mt-0.5 truncate">
              {[pipLabel, fmtRelDays(lead.created_at)].filter(Boolean).join(" · ")}
            </div>
          </div>
          {/* Status pill */}
          <span className={cn(
            "flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-[5px] rounded-full text-[11px] font-bold uppercase tracking-wide border",
            meta.header,
          )}>
            {meta.icon}
            {meta.label}
          </span>
          {/* Close */}
          <button
            onClick={() => onOpenChange(false)}
            className="w-8 h-8 rounded-md grid place-items-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors flex-shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </header>

        {/* ── Body ── */}
        <div className="flex-1 overflow-y-auto px-[22px] py-[18px] flex flex-col gap-[22px]">

          {/* Update status */}
          <section>
            <SectionLabel>Update status</SectionLabel>
            <div className="flex flex-wrap gap-2">
              {ALL_STATUSES.map((s) => {
                const m = STATUS_META[s];
                const isActive = s === lead.status;
                const isBlocked = s === "approved" && !!approvalBlockedBy && !isActive;
                return (
                  <button
                    key={s}
                    type="button"
                    disabled={isBlocked}
                    title={
                      isBlocked
                        ? `${approvalBlockedBy} is already approved for this listing`
                        : undefined
                    }
                    onClick={() => !isActive && !isBlocked && onUpdateStatus(s)}
                    className={cn(
                      "inline-flex items-center gap-1.5 px-3 py-[6px] rounded-full text-[11.5px] font-semibold border transition-colors",
                      isActive
                        ? m.active
                        : "bg-muted border-border text-muted-foreground hover:text-foreground hover:border-border/70",
                      isBlocked &&
                        "opacity-40 cursor-not-allowed hover:text-muted-foreground hover:border-border",
                    )}
                  >
                    {m.icon}
                    {m.label}
                  </button>
                );
              })}
            </div>
            {approvalBlockedBy && lead.status !== "approved" && (
              <p className="text-xs text-muted-foreground mt-2">
                {approvalBlockedBy} is approved for this listing — move them off
                Approved to approve someone else.
              </p>
            )}
          </section>

          {/* Applying for */}
          {listing && (
            <section>
              <SectionLabel>Applying for</SectionLabel>
              <div className="grid grid-cols-[112px_1fr] gap-3.5 items-center">
                <div
                  className="w-[112px] aspect-[4/3] rounded-[10px] bg-muted border border-border bg-cover bg-center flex-shrink-0 overflow-hidden"
                  style={heroPhoto ? { backgroundImage: `url('${heroPhoto}')` } : {}}
                >
                  {!heroPhoto && (
                    <div className="w-full h-full flex items-center justify-center">
                      <Building2 className="w-8 h-8 text-muted-foreground/20" />
                    </div>
                  )}
                </div>
                <div className="min-w-0">
                  <div className="text-[18px] font-bold text-foreground leading-snug">{addr1}</div>
                  <div className="text-[12.5px] text-muted-foreground mt-1.5 flex flex-wrap items-center gap-1">
                    {leaseLabel && <span>{leaseLabel}</span>}
                    {showingDate && <><span>·</span><span>Move-in {fmtDate(showingDate)}</span></>}
                    {pip && (
                      <>
                        <span>· via</span>
                        <span className={cn("inline-grid place-items-center w-4 h-4 rounded-[4px] text-[9px] font-black text-white flex-shrink-0", pip.bg)}>
                          {pip.initial}
                        </span>
                        <span>{pipLabel}</span>
                      </>
                    )}
                    <span>· {fmtRelDays(lead.created_at)}</span>
                  </div>
                </div>
              </div>

              {/* Stat tiles */}
              <div className="grid grid-cols-3 gap-2 mt-3.5">
                <div className="bg-muted border border-border rounded-lg p-3">
                  <div className="text-[11px] text-muted-foreground font-semibold mb-1">Monthly rent</div>
                  <div className="text-[17px] font-extrabold text-foreground leading-tight">
                    {listing.rent_amount ? fmtMoney(listing.rent_amount) : "—"}
                  </div>
                </div>
                <div className="bg-muted border border-border rounded-lg p-3">
                  <div className="text-[11px] text-muted-foreground font-semibold mb-1">Annual income</div>
                  <div className="text-[17px] font-extrabold text-muted-foreground/40 leading-tight">—</div>
                </div>
                <div className="bg-muted border border-border rounded-lg p-3">
                  <div className="text-[11px] text-muted-foreground font-semibold mb-1">Credit score</div>
                  <div className="text-[17px] font-extrabold text-muted-foreground/40 leading-tight">—</div>
                </div>
              </div>
            </section>
          )}

          {/* Screening — shown with available data */}
          <section>
            <SectionLabel>Screening</SectionLabel>
            <div className="space-y-3">
              {[
                {
                  icon: <span className="text-[15px]">💳</span>,
                  name: "Credit score",
                  sub: "Threshold 650+",
                  badge: null,
                  badgeClass: "bg-muted text-muted-foreground/50 border-border",
                  badgeLabel: "—",
                },
                {
                  icon: <span className="text-[15px]">💰</span>,
                  name: "Income vs 3× rent",
                  sub: "Income data not collected",
                  badge: null,
                  badgeClass: "bg-muted text-muted-foreground/50 border-border",
                  badgeLabel: "—",
                },
                {
                  icon: <span className="text-[15px]">🛡️</span>,
                  name: "Background check",
                  sub: "Pending applicant action",
                  badge: false,
                  badgeClass: "bg-amber-400/15 text-amber-400 border-amber-400/28",
                  badgeLabel: "⏱ Pending",
                },
              ].map((row) => (
                <div key={row.name} className="grid grid-cols-[32px_1fr_auto] gap-3 items-center">
                  <div className="w-8 h-8 rounded-[7px] bg-muted border border-border grid place-items-center text-muted-foreground flex-shrink-0">
                    {row.icon}
                  </div>
                  <div>
                    <div className="text-[13px] font-semibold text-foreground">{row.name}</div>
                    <div className="text-[11.5px] text-muted-foreground mt-0.5">{row.sub}</div>
                  </div>
                  <span className={cn(
                    "text-[10.5px] font-bold uppercase tracking-[0.04em] px-2.5 py-1 rounded-full border whitespace-nowrap",
                    row.badgeClass,
                  )}>
                    {row.badgeLabel}
                  </span>
                </div>
              ))}
            </div>
          </section>

          {/* Contact */}
          {(lead.email || lead.phone) && (
            <section>
              <SectionLabel>Contact</SectionLabel>
              <div className="space-y-1.5">
                {lead.email && (
                  <div className="flex items-center gap-2.5 text-[13px] text-foreground py-1">
                    <Mail className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                    <a href={`mailto:${lead.email}`} className="hover:text-primary transition-colors">{lead.email}</a>
                  </div>
                )}
                {lead.phone && (
                  <div className="flex items-center gap-2.5 text-[13px] text-foreground py-1">
                    <Phone className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                    <a href={`tel:${lead.phone}`} className="hover:text-primary transition-colors">{lead.phone}</a>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Note from applicant */}
          {note && (
            <section>
              <SectionLabel>Note from applicant</SectionLabel>
              <div className="border-l-[3px] border-border pl-3.5 text-[13px] text-foreground leading-relaxed">
                {note}
              </div>
            </section>
          )}
        </div>

        {/* ── Footer ── */}
        <footer className="flex gap-2 px-[22px] py-3.5 border-t border-border bg-card flex-shrink-0">
          <a
            href={lead.phone ? `sms:${lead.phone}` : undefined}
            className={cn(
              "flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg text-[13.5px] font-semibold border border-border text-foreground bg-transparent hover:bg-muted transition-colors",
              !lead.phone && "opacity-40 pointer-events-none",
            )}
          >
            <Phone className="w-4 h-4" /> Send text message
          </a>
          <a
            href={lead.email ? `mailto:${lead.email}` : undefined}
            className={cn(
              "flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg text-[13.5px] font-semibold text-white bg-destructive hover:bg-destructive/90 transition-colors",
              !lead.email && "opacity-40 pointer-events-none",
            )}
          >
            <Mail className="w-4 h-4" /> Send email
          </a>
        </footer>
      </SheetContent>
    </Sheet>
  );
}
